import React, { useState, useEffect } from 'react';
import { ArrowRight, Zap, Loader2, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { sendToTelegram } from '../services/telegramService';
import { getRecaptchaToken } from '../services/recaptcha';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    segment: '',
    budget: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'
  const [captchaText, setCaptchaText] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const controls = useAnimation();
  const navigate = useNavigate();

  const heroContent = [
    {
      title: 'Smart Investing Made Simple',
      description: 'Expert guidance to help you make informed investment decision and grow your wealth with confidence.',
      cta: 'Get Started',
      color: 'text-primary-600',
    },
    {
      title: 'Maximize Your Returns',
      description: 'Access professional market analysis and investment strategies tailored to your financial goals.',
      cta: 'Learn More',
      color: 'text-secondary-600',
    },
    {
      title: 'Your Financial Future',
      description: 'Start building a secure financial future with our expert investment advice and portfolio management.',
      cta: 'Start Now',
      color: 'text-primary-600',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroContent.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    controls.start('visible');
  }, [controls]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Manual captcha validation (alphanumeric)
    if (!captchaInput || captchaInput.trim().toLowerCase() !== captchaText.toLowerCase()) {
      setCaptchaError(true);
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Prepare form data
      const formSubmissionData = {
        ...formData,
        formType: 'Hero Section Form',
        timestamp: new Date().toISOString()
      };
      
      console.log('Submitting form data:', formSubmissionData);
      
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken('hero_submit');
      const payload = { ...formSubmissionData, recaptchaToken };
      
      // Send to Telegram
      const result = await sendToTelegram(payload);
      
      if (result.success) {
        console.log('Form submitted successfully');
        setSubmitStatus('success');
        
        // Reset form
        setFormData({
          name: '',
          phone: '',
          segment: '', // force user to select again
          budget: ''
        });

        // Redirect to thank you page
        navigate('/thank-you');
      } else {
        console.error('Telegram submission failed:', result.error);
        // Still show success to user but log the error
        setSubmitStatus('success');
        navigate('/thank-you');
      }
      
      // Reset status message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
      
    } catch (error) {
      console.error('Error in form submission:', error);
      // Show success to user even if Telegram fails
      setSubmitStatus('success');
      
      // Still reset the form on error
      setFormData({
        name: '',
        phone: '',
        segment: '',
        budget: ''
      });
      // Redirect to thank you page regardless
      navigate('/thank-you');
      
      // Reset status message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
      setCaptchaInput('');
      setCaptchaError(false);
      // regenerate captcha for next submission
      generateCaptcha();
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Captcha helpers
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let str = '';
    for (let i = 0; i < 5; i++) str += chars.charAt(Math.floor(Math.random() * chars.length));
    setCaptchaText(str);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  // Static SVG paths - removed infinite animation to fix Core Web Vitals
  const pathVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: { duration: 2, ease: 'easeInOut' },
    },
  };

  return (
    <div className="relative min-h-screen bg-white text-gray-800 overflow-hidden pt-24 md:pt-0">
      {/* Animated SVG Background Lines */}
      <svg
        className="absolute inset-0 w-full h-full z-0"
        preserveAspectRatio="none"
        viewBox="0 0 1440 900"
      >
        {/* Red Line (connects to form middle) */}
        <motion.path
          d="M0,700 C200,600 400,750 600,650 S1000,700 1200,650 L1200,550"
          fill="none"
          stroke="#ef4444"
          strokeWidth="4"
          strokeLinecap="round"
          variants={pathVariants}
          initial="hidden"
          animate="visible"
        />
        {/* Green Line (connects to form middle) */}
        <motion.path
          d="M0,750 C200,650 400,800 600,700 S1000,750 1200,700 L1200,550"
          fill="none"
          stroke="#22c55e"
          strokeWidth="4"
          strokeLinecap="round"
          variants={pathVariants}
          initial="hidden"
          animate="visible"
        />
      </svg>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 lg:py-16 flex flex-col lg:flex-row items-center justify-between min-h-screen relative z-10">
        {/* Left Content */}
        <motion.div
          className="lg:w-1/2 space-y-8 text-center lg:text-left"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          {/* Badge */}
          <motion.div
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary-600 to-secondary-600 px-4 py-1.5 rounded-full mb-4 border border-primary-300/30"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ scale: 1.03, boxShadow: '0 4px 20px -5px rgba(124, 58, 237, 0.3)' }}
          >
            <Zap className="w-3.5 h-3.5 text-yellow-200" />
            <span className="text-xs font-medium text-white tracking-wide">SEBI Registered Research Analyst</span>
          </motion.div>

          {/* Animated Headline */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-secondary-600">
                  {heroContent[currentSlide].title}
                </span>
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">{heroContent[currentSlide].description}</p>
            </motion.div>
          </AnimatePresence>

          {/* CTA & High-Converting Quick Pricing */}
          <motion.div
            className="space-y-5 mt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {/* Primary Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3.5 justify-center lg:justify-start">
              <button
                type="button"
                onClick={() => navigate('/checkout')}
                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-lg font-medium transition-all flex items-center justify-center space-x-2 text-sm shadow-md hover:shadow-lg"
              >
                <Zap className="w-4 h-4 text-yellow-300" />
                <span>Subscribe Online</span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollToSection('services')}
                className="px-6 py-3 bg-white text-gray-800 border border-gray-200 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm shadow-xs flex items-center justify-center space-x-2"
              >
                <span>View Plans & Pricing</span>
              </button>
            </div>

            {/* Quick Pricing Cards for Immediate Purchase */}
            <div className="pt-1">
              <div className="flex items-center justify-between mb-2.5 text-xs text-gray-500 font-semibold uppercase tracking-wider">
                <span className="flex items-center gap-1.5 text-gray-700">
                  <Zap className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                  <span>Featured Advisory Packages:</span>
                </span>
                <button
                  type="button"
                  onClick={() => scrollToSection('services')}
                  className="text-primary-600 hover:text-primary-700 hover:underline text-xs capitalize font-medium flex items-center gap-0.5"
                >
                  View all 6 plans →
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {/* Plan 1: Index Option */}
                <div
                  onClick={() => navigate('/checkout?service=index-option&plan=Monthly')}
                  className="bg-white/95 backdrop-blur-sm border border-primary-200 hover:border-primary-500 rounded-xl p-3 shadow-sm hover:shadow-md transition-all cursor-pointer group text-left relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 bg-primary-600 text-[10px] text-white font-bold px-1.5 py-0.5 rounded-bl">
                    Hot 🔥
                  </div>
                  <div className="text-xs font-bold text-gray-800 truncate pr-6">Index Option</div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-sm font-extrabold text-primary-700">₹12,500</span>
                    <span className="text-[10px] text-gray-500">/mo</span>
                  </div>
                  <div className="mt-2 text-[11px] font-semibold text-primary-600 group-hover:text-primary-700 flex items-center gap-1">
                    <span>Buy Now</span>
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>

                {/* Plan 2: Stock Option */}
                <div
                  onClick={() => navigate('/checkout?service=stock-option&plan=Monthly')}
                  className="bg-white/95 backdrop-blur-sm border border-gray-200 hover:border-primary-500 rounded-xl p-3 shadow-sm hover:shadow-md transition-all cursor-pointer group text-left relative overflow-hidden"
                >
                  <div className="text-xs font-bold text-gray-800 truncate">Stock Option</div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-sm font-extrabold text-primary-700">₹12,500</span>
                    <span className="text-[10px] text-gray-500">/mo</span>
                  </div>
                  <div className="mt-2 text-[11px] font-semibold text-primary-600 group-hover:text-primary-700 flex items-center gap-1">
                    <span>Buy Now</span>
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>

                {/* Plan 3: Stock Cash Delivery */}
                <div
                  onClick={() => navigate('/checkout?service=stock-cash&plan=Monthly')}
                  className="bg-white/95 backdrop-blur-sm border border-emerald-200 hover:border-emerald-500 rounded-xl p-3 shadow-sm hover:shadow-md transition-all cursor-pointer group text-left relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 bg-emerald-600 text-[10px] text-white font-bold px-1.5 py-0.5 rounded-bl">
                    Delivery
                  </div>
                  <div className="text-xs font-bold text-gray-800 truncate pr-6">Stock Cash Delivery</div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-sm font-extrabold text-emerald-700">₹12,500</span>
                    <span className="text-[10px] text-gray-500">/mo</span>
                  </div>
                  <div className="mt-2 text-[11px] font-semibold text-emerald-600 group-hover:text-emerald-700 flex items-center gap-1">
                    <span>Buy Now</span>
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-3 text-[11px] text-gray-500 justify-center lg:justify-start">
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  Instant Dashboard Access
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  SEBI Compliant Invoice
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  100% Secure Checkout
                </span>
              </div>
            </div>
          </motion.div>

          {/* Stats removed as per updated content requirements */}

        </motion.div>

        {/* Right Form */}
        <motion.div
          className="lg:w-96 w-full mt-12 lg:mt-0 bg-white border-t lg:border-t-0 lg:border-l border-gray-100 shadow-lg flex-shrink-0 relative z-20"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.5 }}
        >
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 p-4 text-white">
            <h3 className="text-lg font-semibold">Free Consultation</h3>
            <p className="text-xs text-primary-100 mt-0.5">Fill the form and we'll get back to you shortly</p>
          </div>
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm mb-4 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                Thank you! We'll get back to you shortly.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                Oops! Something went wrong. Please try again.
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Your Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                onInvalid={(e) => e.currentTarget.setCustomValidity('Please enter your name')}
                onInput={(e) => e.currentTarget.setCustomValidity('')}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter your name"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                inputMode="numeric"
                pattern="^[0-9]{10}$"
                onInvalid={(e) => e.currentTarget.setCustomValidity('Please fill a valid 10-digit number')}
                onInput={(e) => e.currentTarget.setCustomValidity('')}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter your phone number"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Segment</label>
              <select
                name="segment"
                value={formData.segment}
                onChange={handleInputChange}
                required
                onInvalid={(e) => e.currentTarget.setCustomValidity('Please select segment')}
                onInput={(e) => e.currentTarget.setCustomValidity('')}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={isSubmitting}
              >
                <option value="" disabled>
                  Select Investment Segment
                </option>
                <option value="Stock Cash">Stock Cash</option>
<option value="Index Options">Index Options/Future</option>
<option value="MCX (Commodities)">MCX (Commodities)</option>
<option value="Stock Option/Future">Stock Option/Future</option>
<option value="Trading Academy">Trading Academy</option>

              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Investment Budget</label>
              <select
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
                required
                onInvalid={(e) => e.currentTarget.setCustomValidity('Please select budget')}
                onInput={(e) => e.currentTarget.setCustomValidity('')}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="" disabled>
                  Select Budget Range
                </option>
                <option value="25k-50k">₹25K - ₹50K</option>
                <option value="50k-100k">₹50K - ₹1L</option>
                <option value="100k-200k">₹1L - ₹2L</option>
                <option value="200k+">Above ₹2L</option>
              </select>
            </div>

            {/* Manual Captcha Checkbox */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-600">Captcha Verification</label>
              <div className="flex items-center gap-2">
                <div className="select-none tracking-widest px-3 py-2 bg-gray-100 border border-gray-200 rounded font-mono text-sm">
                  {captchaText}
                </div>
                <button
                  type="button"
                  onClick={generateCaptcha}
                  className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Refresh
                </button>
              </div>
              <input
                type="text"
                value={captchaInput}
                onChange={(e) => {
                  setCaptchaInput(e.target.value);
                  if (captchaError) setCaptchaError(false);
                }}
                placeholder="Enter the text shown above"
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${captchaError ? 'border-red-400' : 'border-gray-200'}`}
                disabled={isSubmitting}
              />
              {captchaError && (
                <p className="text-xs text-red-600">Captcha is incorrect. Please try again.</p>
              )}
            </div>

            <button
              type="submit"
              className="w-full mt-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-2.5 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center space-x-2"
            >
              <span>Get Call Back</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </form>
        </motion.div>
      </div>

      {/* Slider Indicators */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center space-x-2 z-30">
        {heroContent.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-1.5 rounded-full transition-all relative overflow-hidden ${
              currentSlide === index ? 'w-8 bg-primary-600' : 'w-3 bg-gray-200'
            }`}
            aria-label={`Go to slide ${index + 1}`}
            whileHover={{ scale: 1.2 }}
          >
            {currentSlide === index && (
              <motion.span
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full"
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 6, ease: 'linear' }}
                onAnimationComplete={() => {
                  if (currentSlide === index) {
                    setCurrentSlide((prev) => (prev + 1) % heroContent.length);
                  }
                }}
              />
            )}
          </motion.button>
        ))}
      </div>

      {/* Mobile-specific SVG for Lines */}
      <svg
        className="lg:hidden absolute inset-0 w-full h-full z-0"
        preserveAspectRatio="none"
        viewBox="0 0 375 667"
      >
        {/* Red Line (connects to form middle on mobile) */}
        <motion.path
          d="M0,600 C50,550 100,600 150,550 S250,600 300,550 L300,400"
          fill="none"
          stroke="#ef4444"
          strokeWidth="4"
          strokeLinecap="round"
          variants={pathVariants}
          initial="hidden"
          animate="visible"
        />
        {/* Green Line (connects to form middle on mobile) */}
        <motion.path
          d="M0,620 C50,570 100,620 150,570 S250,620 300,570 L300,400"
          fill="none"
          stroke="#22c55e"
          strokeWidth="4"
          strokeLinecap="round"
          variants={pathVariants}
          initial="hidden"
          animate="visible"
        />
      </svg>
    </div>
  );
};

export default Hero;